import { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import makeAnimated from 'react-select/animated';
import Select from 'react-select';
import { useTranslate } from 'hooks/useTranslate';

import { State } from 'redux/initialState';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { store } from 'redux/store';
import Button from 'modules/_shared/components/Buttons';
import UploadDocument from 'modules/documents/components/UploadDocument';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { DocumentUpload } from 'types';
import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import { updateTradingOfferOperation } from '../../redux/tradingOfferOperationActions';
import {
  TRADING_OFFER_OPERATION_DOCUMENT_ACCEPT,
  TRADING_OFFER_OPERATION_DOCUMENT_MAX_SIZE_BYTES,
  TRADING_OFFER_OPERATION_DOCUMENT_OPTIONS,
} from '../../constants/tradingOfferOperationDocumentTypes';
import { OperationDocumentRole } from '../../pages/TradingOfferOperationDetail/types/TradingOfferOperationDetailContextType';

type AddTradingOfferDocumentationModalProps = {
  operationId: string;
  societyId: string;
  isAdmin: boolean;
  userDocumentRole: OperationDocumentRole | null;
  initialDocumentRole?: OperationDocumentRole;
  sellerName: string;
  buyerDocuments: string[];
  sellerDocuments: string[];
  onSuccess?: () => void;
};

const AddTradingOfferDocumentationModal: FC<
  AddTradingOfferDocumentationModalProps
> = ({
  operationId,
  societyId,
  isAdmin,
  userDocumentRole,
  initialDocumentRole,
  sellerName,
  buyerDocuments = [],
  sellerDocuments = [],
  onSuccess,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const user = useSelector((state: State) => state.user);

  const [documentRole, setDocumentRole] = useState<OperationDocumentRole>(
    initialDocumentRole ?? userDocumentRole ?? 'buyer'
  );
  const [documentType, setDocumentType] = useState<string>('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const handleSubmit = async () => {
    if (!file) {
      errorSwal(t('SelectFile'));
      return;
    }

    if (file.size > TRADING_OFFER_OPERATION_DOCUMENT_MAX_SIZE_BYTES) {
      errorSwal(t('FileTooLargeMax10MB'));
      return;
    }

    setIsLoading(true);
    try {
      const fileType = fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
      const linkedTo = getLinkedTo({
        tradingOfferOperationId: operationId,
      });
      const newDocument: DocumentUpload = {
        file,
        date: new Date(),
        size: file.size,
        name: file.name,
        author: user?.['_id'],
        society: societyId,
        fileType,
        category: documentTypes.DOCUMENTS,
        subcategory: documentType,
        description: description || t('OperationDocumentation'),
        linkedTo,
      };

      const uploadedDoc = await store.dispatch(addDocument(newDocument));
      if (!uploadedDoc?._id) {
        throw new Error('Document upload failed');
      }

      const operationData =
        documentRole === 'buyer'
          ? { buyerDocuments: [...buyerDocuments, uploadedDoc._id] }
          : { sellerDocuments: [...sellerDocuments, uploadedDoc._id] };

      await (dispatch as any)(
        updateTradingOfferOperation(operationId, operationData as any)
      );

      successSwal(t('DocumentUploadedSuccessfully'));
      handleClose();
      onSuccess?.();
    } catch (error) {
      errorSwal(t('ErrorUploadingFile'));
    } finally {
      setIsLoading(false);
    }
  };

  const introText =
    documentRole === 'buyer'
      ? t('AddDocumentationBuyerIntro')
      : t('AddDocumentationSellerIntro', {
          sellerName: sellerName || t('Seller'),
        });

  const animatedComponents = makeAnimated();

  const documentRoleOptions = useMemo(
    () => [
      {
        value: 'buyer' as const,
        label: isAdmin ? t('Buyer') : t('BuyerYou'),
      },
      {
        value: 'seller' as const,
        label: `${t('Seller')}${sellerName ? ` (${sellerName})` : ''}`,
      },
    ],
    [t, sellerName, isAdmin]
  );

  const documentTypeOptions = useMemo(
    () =>
      TRADING_OFFER_OPERATION_DOCUMENT_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.label),
      })),
    [t]
  );

  const selectedDocumentRoleOption =
    documentRoleOptions.find((o) => o.value === documentRole) ?? null;
  const selectedDocumentTypeOption =
    documentTypeOptions.find((o) => o.value === documentType) ?? null;

  return (
    <>
      <Modal.Header>
        <h5 className="modal-title mb-0">
          <em className="icon ni ni-file-docs mr-2" />
          {t('AddDocumentation')}
        </h5>
        <a
          className="close cursor-pointer"
          onClick={handleClose}
          aria-label={t('Close')}
        >
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body>
        <p className="text-soft small mb-4">{introText}</p>

        {isAdmin && (
          <div className="form-group mb-4">
            <label className="form-label" htmlFor="docRole">
              {t('DocumentationFor')}
            </label>
            <Select
              id="docRole"
              closeMenuOnSelect
              value={selectedDocumentRoleOption}
              className="react-select react-select-lg"
              options={documentRoleOptions}
              components={animatedComponents}
              onChange={(opt) =>
                setDocumentRole(
                  (opt?.value as OperationDocumentRole) ?? 'buyer'
                )
              }
              isMulti={false}
            />
          </div>
        )}

        <div className="form-group mb-4">
          <label className="form-label" htmlFor="docTipo">
            {t('DocumentType')}
          </label>
          <Select
            id="docTipo"
            closeMenuOnSelect
            value={selectedDocumentTypeOption}
            className="react-select react-select-lg"
            options={documentTypeOptions}
            components={animatedComponents}
            onChange={(opt) => setDocumentType(opt?.value ?? '')}
            isMulti={false}
            placeholder={t('DocumentType')}
          />
        </div>

        <div className="form-group mb-4">
          <label className="form-label" htmlFor="docDescripcion">
            {t('DescriptionOptional')}
          </label>
          <input
            type="text"
            className="form-control"
            id="docDescripcion"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('DocumentDescriptionPlaceholder')}
            maxLength={200}
          />
        </div>

        <UploadDocument
          label="File"
          file={file}
          setFile={setFile}
          accept={TRADING_OFFER_OPERATION_DOCUMENT_ACCEPT}
          mandatory
          inputLabel={t('OperationDocFormNote')}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!file || isLoading}
        >
          <em className="icon ni ni-upload mr-1" />
          {t('UploadDocumentation')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default AddTradingOfferDocumentationModal;
